export interface RankStats {
  sales_90d: number;
  favorites_count: number;
}

interface Rankable {
  id: string;
  seller_id: string;
  average_rating: number | null;
  review_count: number | null;
  visibility: { priority_weight: number } | null;
}

/** Popularity score: sales, favorites, ratings and paid boosts. Always > 0. */
export function popularityScore(p: Rankable, stats?: RankStats): number {
  const sales = stats?.sales_90d ?? 0;
  const favs = stats?.favorites_count ?? 0;
  const rating = (p.average_rating ?? 0) * Math.log(1 + (p.review_count ?? 0));
  const boost = p.visibility?.priority_weight ?? 0;
  return 1 + 3 * sales + 2 * favs + rating + boost;
}

/** Weighted random order (Efraimidis-Spirakis) with a per-store cap. */
export function rankProducts<T extends Rankable>(
  products: T[],
  statsMap: Map<string, RankStats>,
  limit = 8,
  perStore = 2,
): T[] {
  const keyed = products
    .map((p) => ({ p, k: Math.pow(Math.random(), 1 / popularityScore(p, statsMap.get(p.id))) }))
    .sort((a, b) => b.k - a.k)
    .map((x) => x.p);

  const counts = new Map<string, number>();
  const picked: T[] = [];
  for (const p of keyed) {
    const c = counts.get(p.seller_id) ?? 0;
    if (c >= perStore) continue;
    counts.set(p.seller_id, c + 1);
    picked.push(p);
    if (picked.length >= limit) break;
  }
  // Fill if too few stores
  if (picked.length < Math.min(6, keyed.length)) {
    for (const p of keyed) {
      if (picked.length >= limit) break;
      if (!picked.includes(p)) picked.push(p);
    }
  }
  return picked;
}
